"use strict";

(() => {
	class Electricity_Consumption_Calculator {
		/** @var {HTMLElement} */
		#$widget;


		/** @var {HTMLInputElement} */
		#$houseSizeInput;

		/** @var {HTMLInputElement} */
		#$residentsInput;

		/** @var {HTMLInputElement} */
		#$habitsInputs;

		/** @var {HTMLInputElement} */
		#$priceInput;

		/** @var {HTMLSelectElement} */
		#$priceUnitInput;

		/** @var {HTMLInputElement} */
		#$moreOptionsInput;

		/** @var {HTMLInputElement} */
		#$includeHeatingInput;

		/** @var {HTMLInputElement} */
		#$includeWaterHeatingInput;

		/** @var {HTMLInputElement} */
		#$includeCookingInput;

		/** @var {HTMLInputElement} */
		#$includeLightingInput;

		/** @var {NodeListOf<HTMLInputElement>} */
		#$heatingTypeInputs;

		/** @var {NodeListOf<HTMLInputElement>} */
		#$waterHeatingTypeInputs;

		/** @var {HTMLInputElement} */
		#$addConsumptionInput;

		/** @var {HTMLSelectElement} */
		#$addConsumptionUnitInput;


		/** @var {HTMLElement} */
		#$monthlyPowerResult;

		/** @var {HTMLElement} */
		#$monthlyPriceResult;

		/** @var {HTMLElement} */
		#$yearlyPowerResult;

		/** @var {HTMLElement} */
		#$yearlyPriceResult;


		constructor($widget) {
			this.#$widget = $widget;
		}

		initialize() {
			// Initialize elements.
			this.#$houseSizeInput           = this.#$widget.querySelector(".dan-input-house-size");
			this.#$residentsInput           = this.#$widget.querySelector(".dan-input-residents");
			this.#$habitsInputs             = this.#$widget.querySelector(".dan-input-habits");
			this.#$priceInput               = this.#$widget.querySelector(".dan-input-price");
			this.#$priceUnitInput           = this.#$widget.querySelector(".dan-input-price-unit");
			this.#$moreOptionsInput         = this.#$widget.querySelector(".dan-input-more-options");
			this.#$includeHeatingInput      = this.#$widget.querySelector(".dan-input-include-heating");
			this.#$includeWaterHeatingInput = this.#$widget.querySelector(".dan-input-include-water-heating");
			this.#$includeCookingInput      = this.#$widget.querySelector(".dan-input-include-cooking");
			this.#$includeLightingInput     = this.#$widget.querySelector(".dan-input-include-lighting");
			this.#$heatingTypeInputs        = this.#$widget.querySelectorAll(".dan-inputs-heating-type input[type='radio']");
			this.#$waterHeatingTypeInputs   = this.#$widget.querySelectorAll(".dan-inputs-water-heating-type input[type='radio']");
			this.#$addConsumptionInput      = this.#$widget.querySelector(".dan-input-add-consumption");
			this.#$addConsumptionUnitInput  = this.#$widget.querySelector(".dan-input-add-consumption-unit");

			this.#$monthlyPowerResult = this.#$widget.querySelector(".dan-result-monthly .dan-result-power");
			this.#$monthlyPriceResult = this.#$widget.querySelector(".dan-result-monthly .dan-result-price");
			this.#$yearlyPowerResult  = this.#$widget.querySelector(".dan-result-yearly .dan-result-power");
			this.#$yearlyPriceResult  = this.#$widget.querySelector(".dan-result-yearly .dan-result-price");

			// Add handlers to events.
			this.#$houseSizeInput.addEventListener("input", this.#refreshResults.bind(this));
			this.#$residentsInput.addEventListener("input", this.#refreshResults.bind(this));
			this.#$habitsInputs.addEventListener("input", this.#refreshResults.bind(this));
			this.#$priceInput.addEventListener("input", this.#refreshResults.bind(this));
			this.#$priceUnitInput.addEventListener("change", this.#onPriceUnitChanged.bind(this));
			this.#$moreOptionsInput.addEventListener("change", this.#refreshResults.bind(this));
			this.#$includeHeatingInput.addEventListener("change", this.#refreshResults.bind(this));
			this.#$includeWaterHeatingInput.addEventListener("change", this.#refreshResults.bind(this));
			this.#$includeCookingInput.addEventListener("change", this.#refreshResults.bind(this));
			this.#$includeLightingInput.addEventListener("change", this.#refreshResults.bind(this));
			this.#$addConsumptionInput.addEventListener("input", this.#refreshResults.bind(this));
			this.#$addConsumptionUnitInput.addEventListener("change", this.#refreshResults.bind(this));

			for (const $input of this.#$heatingTypeInputs) {
				$input.addEventListener("change", this.#refreshResults.bind(this));
			}

			for (const $input of this.#$waterHeatingTypeInputs) {
				$input.addEventListener("change", this.#refreshResults.bind(this));
			}

			// Initialize range inputs.
			const $rangeGroups = this.#$widget.querySelectorAll(".dan-form-range");

			for (const $group of $rangeGroups) {
				$group.rangeInput = new Dan_RangeInput($group);
				$group.rangeInput.initialize();
			}

			// Initialize toggle sections.
			const $toggleSections = this.#$widget.querySelectorAll(".dan-toggle-section");

			for (const $section of $toggleSections) {
				$section.toggleSection = new Dan_ToggleSection($section);
				$section.toggleSection.initialize();
			}

			// Initialize tooltips.
			const $tooltips = this.#$widget.querySelectorAll("[data-tooltip]");

			if ($tooltips.length > 0) {
				// noinspection JSUnresolvedReference
				tippy($tooltips, {
					content: ($element) => $element.getAttribute("data-tooltip"),
					allowHTML: true
				});
			}

			// Refresh results on load.
			this.#refreshResults();
		}

		#refreshResults() {
			// Get and check inputs.
			const inputs = this.#getInputs();

			if (
				inputs.houseSize === null
				|| inputs.residents === null
				|| inputs.habits === null
				|| inputs.price === null
			) {
				this.#setResults(null);
				return;
			}

			// Calculate consumptions.
			const consumptions = this.#calcMonthlyConsumptionsInKw(inputs);
			let totalMonthlyKw = (
				consumptions.heating
				+ consumptions.waterHeating
				+ consumptions.cooking
				+ consumptions.lighting
				+ consumptions.additionalConsumption
			);

			totalMonthlyKw += (totalMonthlyKw * inputs.habits / 100);

			const totalYearlyKw = (totalMonthlyKw * 12);

			// Calculate prices.
			const monthlyPrice = this.#calcPrice(totalMonthlyKw, inputs.price, inputs.priceUnit);
			const yearlyPrice  = this.#calcPrice(totalYearlyKw, inputs.price, inputs.priceUnit);

			// Set results.
			this.#setResults({
				monthlyPower: totalMonthlyKw,
				monthlyPrice: monthlyPrice,
				yearlyPower: totalYearlyKw,
				yearlyPrice: yearlyPrice
			});
		}

		#getInputs() {
			// House size.
			let houseSize = parseInt(this.#$houseSizeInput.value);

			if (houseSize < 0) {
				houseSize = null;
			}

			// Residents.
			let residents = parseInt(this.#$residentsInput.value);

			if (residents < 1) {
				residents = null;
			}

			// Habits.
			const habits = parseInt(this.#$habitsInputs.value);

			// Price.
			let price;
			price = this.#$priceInput.value || this.#$priceInput.placeholder;
			price = parseFloat(price);

			if (price < 0) {
				price = null;
			}

			// Price unit.
			const priceUnit = this.#$priceUnitInput.value;

			// More options.
			const moreOptions = this.#$moreOptionsInput.checked;

			// Include heating.
			const includeHeating = (moreOptions)
				? this.#$includeHeatingInput.checked
				: true;

			// Heating type.
			let heatingType;

			if (!includeHeating) {
				heatingType = null;
			} else if (!moreOptions) {
				heatingType = "kettle";
			} else {
				for (const $input of this.#$heatingTypeInputs) {
					if ($input.checked) {
						heatingType = $input.value;
						break;
					}
				}
			}

			// Include water heating.
			const includeWaterHeating = (moreOptions)
				? this.#$includeWaterHeatingInput.checked
				: true;

			// Water heating type.
			let waterHeatingType;

			if (!includeWaterHeating) {
				waterHeatingType = null;
			} else if (!moreOptions) {
				waterHeatingType = "kettle";
			} else {
				for (const $input of this.#$waterHeatingTypeInputs) {
					if ($input.checked) {
						waterHeatingType = $input.value;
						break;
					}
				}
			}

			// Include cooking.
			const includeCooking = (moreOptions)
				? this.#$includeCookingInput.checked
				: true;

			// Include lighting.
			const includeLighting = (moreOptions)
				? this.#$includeLightingInput.checked
				: true;

			// Additional consumption.
			let additionalConsumption;
			additionalConsumption = this.#$addConsumptionInput.value || this.#$addConsumptionInput.placeholder;
			additionalConsumption = parseFloat(additionalConsumption);

			if (additionalConsumption < 0) {
				additionalConsumption = null;
			}

			// Additional consumption unit.
			const additionalConsumptionUnit = this.#$addConsumptionUnitInput.value;

			// Return.
			return {
				houseSize,
				residents,
				habits,
				price,
				priceUnit,
				includeHeating,
				heatingType,
				includeWaterHeating,
				waterHeatingType,
				includeCooking,
				includeLighting,
				additionalConsumption,
				additionalConsumptionUnit
			};
		}

		#calcMonthlyConsumptionsInKw(inputs) {
			// Heating.
			let heating;

			if (inputs.includeHeating) {
				const heatingKwM2Month = (inputs.heatingType === "kettle")
					? (110 / 12)
					: (36.6 / 12);

				heating = (inputs.houseSize * heatingKwM2Month);
			} else {
				heating = 0;
			}

			// Water heating.
			let waterHeating;

			if (inputs.includeWaterHeating) {
				const waterHeatingKwResidentMonth = (inputs.waterHeatingType === "kettle")
					? (678 / 12)
					: (226 / 12);

				waterHeating = (inputs.residents * waterHeatingKwResidentMonth);
			} else {
				waterHeating = 0;
			}

			// Cooking.
			const cookingKwFirstResidentMonth = (200 / 12);
			const cookingKwOtherResidentMonth = (160 / 12);
			const cooking                     = (inputs.includeCooking)
				? (cookingKwFirstResidentMonth + (inputs.residents - 1) * cookingKwOtherResidentMonth)
				: 0;

			// Lighting.
			const lightingKwFirstResidentMonth = (1000 / 12);
			const lightingKwOtherResidentMonth = (800 / 12);
			const lighting                     = (inputs.includeLighting)
				? (lightingKwFirstResidentMonth + (inputs.residents - 1) * lightingKwOtherResidentMonth)
				: 0;

			// Additional consumption.
			let additionalConsumption;

			if (inputs.additionalConsumptionUnit === "kwh-month") {
				additionalConsumption = inputs.additionalConsumption;
			} else if (inputs.additionalConsumptionUnit === "kwh-year") {
				additionalConsumption = (inputs.additionalConsumption / 12);
			} else if (inputs.additionalConsumptionUnit === "mwh-year") {
				additionalConsumption = (inputs.additionalConsumption * 1000 / 12);
			} else {
				additionalConsumption = 0;
			}

			// Return.
			return {
				heating,
				waterHeating,
				cooking,
				lighting,
				additionalConsumption
			};
		}

		#calcPrice(consumption, price, priceUnit) {
			let priceKwh;

			if (priceUnit === "kWh") {
				priceKwh = price;
			} else if (priceUnit === "MWh") {
				priceKwh = (price / 1000);
			}

			return (consumption * priceKwh);
		}

		#setResults(results) {
			if (results === null) {
				this.#$monthlyPowerResult.textContent = "-";
				this.#$monthlyPriceResult.textContent = "-";
				this.#$yearlyPowerResult.textContent  = "-";
				this.#$yearlyPriceResult.textContent  = "-";
			} else {
				this.#$monthlyPowerResult.textContent = Dan_Utilities.smartFormat(results.monthlyPower) + " kWh";
				this.#$monthlyPriceResult.textContent = Dan_Utilities.formatPrice(results.monthlyPrice) + " Kč";
				this.#$yearlyPowerResult.textContent  = Dan_Utilities.smartFormat(results.yearlyPower) + " kWh";
				this.#$yearlyPriceResult.textContent  = Dan_Utilities.formatPrice(results.yearlyPrice) + " Kč";
			}
		}

		#onPriceUnitChanged() {
			const inputs = this.#getInputs();

			if (inputs.price === null) {
				return;
			}

			// If unit is MWh now, it was set to kWh before the change.
			if (inputs.priceUnit === "MWh") {
				this.#$priceInput.value = (inputs.price * 1000).toString();
			} else {
				this.#$priceInput.value = (inputs.price / 1000).toString();
			}

			this.#refreshResults();
		}
	}

	// Initialize widgets.
	const $widgets = document.querySelectorAll(".energetiko-electricity-consumption-calculator");

	for (const $widget of $widgets) {
		$widget.calculator = new Electricity_Consumption_Calculator($widget);
		$widget.calculator.initialize();
	}
})();
